import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Hammer, Check, X, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, naira } from "@/lib/api";

const CATS = ["Sleep", "Kitchen", "Bath", "Comfort"];

export default function Furnish() {
  const { setCharacter } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [buyMode, setBuyMode] = useState(true);
  const [cat, setCat] = useState("Sleep");
  const [placing, setPlacing] = useState(null);   // catalogue item being placed
  const [selected, setSelected] = useState(null); // placed instance selected (move/remove)
  const [useItem, setUseItem] = useState(null);    // placed item for actions (live mode)
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get("/home"); setData(r.data); }
    catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const room = data?.room;
  const refresh = (res) => {
    setData((d) => ({ ...d, room: res.room, wallet: res.character ? res.character.wallet : d.wallet }));
    if (res.character) setCharacter(res.character);
  };

  const onCell = async (x, y) => {
    if (!buyMode) return;
    if (placing) {
      setBusy(true);
      try { const { data: res } = await api.post("/home/buy", { item_id: placing.id, x, y }); refresh(res); toast.success(`${placing.name} placed ✓`); setPlacing(null); }
      catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
    } else if (selected) {
      setBusy(true);
      try { const { data: res } = await api.post("/home/move", { instance_id: selected, x, y }); refresh(res); setSelected(null); }
      catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
    }
  };

  const onItem = (it) => {
    if (buyMode) { setPlacing(null); setSelected(selected === it.id ? null : it.id); }
    else if (it.actions?.length) setUseItem(it);
  };

  const removeItem = async (id) => {
    setBusy(true);
    try { const { data: res } = await api.post("/home/remove", { instance_id: id }); refresh(res); toast.success(`Removed · +${naira(res.refund)} back`); setSelected(null); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const doAction = async (it, action) => {
    setBusy(true);
    try { const { data: res } = await api.post("/home/use", { instance_id: it.id, action }); setCharacter(res.character); setData((d) => ({ ...d, needs: res.character.needs })); toast.success(`${action} ✓`); setUseItem(null); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const startPlace = (item) => {
    if (data.wallet < item.price) { toast.error("Money no reach for this one"); return; }
    setSelected(null); setPlacing(item);
    toast(`Tap a spot to place ${item.name}`, { description: `${naira(item.price)} · ${item.w}×${item.h}` });
  };

  if (!data || !room) return null;
  const cells = [];
  for (let y = 0; y < room.rows; y++) for (let x = 0; x < room.cols; x++) cells.push({ x, y });

  return (
    <div className="px-5 pt-6" data-testid="furnish-page">
      <div className="flex items-center gap-3">
        <button data-testid="furnish-back" onClick={() => nav("/room")} className="p-2 rounded-full bg-slate-100"><ArrowLeft size={18} /></button>
        <div className="flex-1">
          <h1 className="font-display text-3xl">Furnish <span className="text-[#16A34A]">Your Crib</span></h1>
          <p className="label-xs text-slate-500">Buy furniture, arrange your space, boost your needs</p>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between bg-white border border-slate-200 rounded-xl p-2.5 shadow-sm">
        <span className="text-[#16A34A] font-display px-1" data-testid="furnish-wallet">{naira(data.wallet)}</span>
        <button data-testid="buymode-toggle" onClick={() => { setBuyMode((b) => !b); setPlacing(null); setSelected(null); }}
          className={`px-4 py-1.5 rounded-full label-xs font-bold flex items-center gap-1.5 ${buyMode ? "bg-[#16A34A] text-white" : "bg-slate-100 text-slate-600"}`}>
          <Hammer size={13} /> {buyMode ? "Buy Mode: ON" : "Buy Mode: OFF"}
        </button>
      </div>

      {/* Room grid */}
      <div className="mt-4 rounded-2xl p-3 border border-amber-200 shadow-inner bg-cover bg-center"
        style={{ backgroundImage: "linear-gradient(rgba(255,251,235,0.55),rgba(255,237,213,0.55)), url('https://static.prod-images.emergentagent.com/jobs/0305d694-6d38-4e56-9f10-ef46437aa1b3/images/89f55bde7ddbe00dfb5f46f9b5832ca8b7fc92f5bdfcec32628b3005479acb00.jpeg')" }}>
        <div className="relative grid gap-1" style={{ gridTemplateColumns: `repeat(${room.cols}, 1fr)` }}>
          {cells.map((c) => (
            <button key={`${c.x}-${c.y}`} data-testid={`cell-${c.x}-${c.y}`} onClick={() => onCell(c.x, c.y)}
              className={`aspect-square rounded-md ${buyMode ? "bg-white/50 border border-dashed border-amber-300" : "bg-white/30"} ${(placing || selected) ? "active:bg-[#16A34A]/30" : ""}`} />
          ))}
          {room.items.map((it) => (
            <motion.button key={it.id} data-testid={`placed-${it.item_id}`} onClick={() => onItem(it)}
              initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} whileTap={{ scale: 0.92 }}
              className={`rounded-lg flex items-center justify-center text-2xl shadow-sm border-2 ${selected === it.id ? "border-[#16A34A] ring-2 ring-[#16A34A]/40" : "border-black/10"}`}
              style={{ gridColumn: `${it.x + 1} / span ${it.w}`, gridRow: `${it.y + 1} / span ${it.h}`, background: it.color, zIndex: 10 }}>
              {it.emoji}
              {buyMode && selected === it.id && (
                <span onClick={(e) => { e.stopPropagation(); removeItem(it.id); }} data-testid={`remove-${it.item_id}`}
                  className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-[#FF0055] text-white flex items-center justify-center shadow z-20"><Trash2 size={12} /></span>
              )}
            </motion.button>
          ))}
        </div>
      </div>

      {placing && (
        <div className="mt-2 text-center label-xs text-[#16A34A] font-bold" data-testid="placing-hint">Placing {placing.name} — tap a free spot · <button onClick={() => setPlacing(null)} className="underline">cancel</button></div>
      )}
      {!buyMode && <p className="mt-2 text-center label-xs text-slate-500">Tap furniture to use it and boost your needs 🧼🍲</p>}

      {/* Catalogue (buy mode only) */}
      {buyMode && (
        <>
          <div className="mt-4 flex gap-1.5 bg-white border border-slate-200 rounded-full p-1 shadow-sm">
            {CATS.map((ct) => (
              <button key={ct} data-testid={`cat-${ct}`} onClick={() => setCat(ct)}
                className={`flex-1 py-2 rounded-full label-xs ${cat === ct ? "bg-[#16A34A] text-white" : "text-slate-500"}`}>{ct}</button>
            ))}
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 mb-4">
            {(data.catalogue[cat] || []).map((item) => {
              const afford = data.wallet >= item.price;
              return (
                <div key={item.id} data-testid={`cat-item-${item.id}`} className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-3xl">{item.emoji}</span>
                    <span className="label-xs text-slate-400">{item.w}×{item.h}</span>
                  </div>
                  <div className="mt-1 text-sm font-semibold leading-tight">{item.name}</div>
                  <div className="label-xs text-[#16A34A] font-bold mt-0.5">{naira(item.price)}</div>
                  {item.actions?.length > 0 && <div className="label-xs text-slate-400 mt-0.5">✨ {item.actions.map((a) => a.label).join(" · ")}</div>}
                  <button data-testid={`place-${item.id}`} disabled={!afford || busy} onClick={() => startPlace(item)}
                    className={`mt-2 w-full py-2 rounded-lg label-xs font-bold ${afford ? "bg-[#16A34A] text-white" : "bg-slate-200 text-slate-400"}`}>
                    {afford ? "Place" : "Need more ₦"}
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Use-action popover (live mode) */}
      <AnimatePresence>
        {useItem && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-40 bg-black/40" onClick={() => setUseItem(null)} />
            <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              className="fixed left-1/2 -translate-x-1/2 bottom-28 z-50 w-[86%] max-w-sm bg-white border border-slate-200 rounded-2xl p-4 shadow-xl" data-testid="use-popover">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-display text-lg">{useItem.emoji} {useItem.name}</h3>
                <button onClick={() => setUseItem(null)} className="p-1.5 rounded-full bg-slate-100"><X size={16} /></button>
              </div>
              <div className="space-y-2">
                {useItem.actions.map((a) => (
                  <button key={a.label} data-testid={`use-${a.label.replace(/\s+/g, "-").toLowerCase()}`} disabled={busy} onClick={() => doAction(useItem, a.label)}
                    className="w-full flex items-center justify-between bg-[#16A34A]/10 border border-[#16A34A]/30 text-[#15803D] rounded-lg px-3 py-2.5 label-xs font-bold">
                    <span className="flex items-center gap-1.5"><Sparkles size={13} /> {a.label}</span>
                    <span>{Object.entries(a.needs).map(([k, v]) => `${v > 0 ? "+" : ""}${v} ${k}`).join(", ")}</span>
                  </button>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
